import { useState } from 'react';
import { LoyaltySettings, Customer, Product } from '../../types';
import { db } from '../../db';
import { normalizePhone } from '../../src/utils/phoneUtils';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status?: string;
  timestamp?: number;
}

export function useLoyalty(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  refreshData: () => Promise<void>,
  customers: Customer[],
  products: Product[]
) {
  const [loyaltySettings, setLoyaltySettings] = useState<LoyaltySettings>({
    mode: 'stamp',
    stampsForReward: 10,
    pointsForReward: 100,
    pointsPerCurrency: 10,
    rewardName: 'Ücretsiz Kahve',
    rewardProductIds: [],
    autoStamp: true,
    stampableCategories: []
  });
  const [pendingReward, setPendingReward] = useState<{ productId: number; productName: string } | null>(null);

  const updateLoyaltySettings = async (settings: LoyaltySettings) => {
    setLoyaltySettings(settings);
    await db.settings.put({ key: 'loyaltySettings', value: settings });
    await addToSyncQueue({
      type: 'setting',
      action: 'update',
      data: { key: 'loyaltySettings', value: settings },
      status: 'pending',
      timestamp: Date.now()
    });
  };

  const addStamp = async (customerId: string, count: number = 1, categoryId?: string, isManual: boolean = false) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    const sc = loyaltySettings.stampableCategories;
    if (!isManual && sc && sc.length > 0) {
      if (!categoryId || !sc.includes(categoryId)) return;
    }
    const newStamps = (customer.stamps || 0) + count;
    const updatedCustomer = { ...customer, stamps: newStamps };
    await db.customers.put(updatedCustomer);
    await addToSyncQueue({
      type: 'customer',
      action: 'update',
      data: updatedCustomer,
    });
    await refreshData();
  };

  const redeemReward = async (customerId: string, selectedProductId?: string) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return;
    const field = loyaltySettings.mode === 'stamp' ? 'stamps' : 'loyaltyPoints';
    const rewardThreshold = loyaltySettings.mode === 'stamp'
      ? loyaltySettings.stampsForReward
      : loyaltySettings.pointsForReward;
    const current = (customer[field] || 0);
    if (current < rewardThreshold) return;
    const updatedCustomer = {
      ...customer,
      [field]: current - rewardThreshold,
      loyaltyRedeemed: (customer.loyaltyRedeemed || 0) + 1
    };
    await db.customers.put(updatedCustomer);
    await addToSyncQueue({
      type: 'customer',
      action: 'update',
      data: updatedCustomer,
    });
    await refreshData();
    const ids = loyaltySettings.rewardProductIds || [];
    const pidToUse = selectedProductId || ids[0];
    if (pidToUse) {
      const rewardProduct = products.find(p => String(p.id) === pidToUse);
      if (rewardProduct) {
        setPendingReward({ productId: rewardProduct.id, productName: rewardProduct.name });
      }
    }
  };

  const addLoyaltyCustomer = async (name: string, phone?: string) => {
    try {
      const normalizedPhone = normalizePhone(phone || '');
      if (normalizedPhone) {
        const duplicate = customers.find(c => normalizePhone(c.phone || '') === normalizedPhone);
        if (duplicate) throw new Error('DUPLICATE_PHONE');
      }
      const newCustomer: Customer = {
        id: crypto.randomUUID(),
        name,
        phone: normalizedPhone,
        stamps: 0,
        loyaltyRedeemed: 0,
        loyaltyOnly: true,
      };
      await db.customers.put(newCustomer);
      await addToSyncQueue({
        type: 'customer',
        action: 'create',
        data: newCustomer,
      });
      await refreshData();
    } catch (err) {
      console.error('LOYALTY ERROR', err);
      throw err;
    }
  };

  return {
    loyaltySettings,
    setLoyaltySettings,
    pendingReward,
    setPendingReward,
    updateLoyaltySettings,
    addStamp,
    redeemReward,
    addLoyaltyCustomer,
  };
}
